import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Paper, Typography, TextField, InputAdornment, Divider, Chip, Checkbox, FormControlLabel
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useComponents } from '../../../hooks/maintenance/useComponent';

const SystemHeader = ({ name, count, selectedCount, onToggleAll }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
    <Typography variant="subtitle1" fontWeight={600}>
      {name} <Chip size="small" label={`${selectedCount}/${count}`} sx={{ ml: 1 }} />
    </Typography>
    <Button size="small" onClick={onToggleAll}>
      {selectedCount === count ? 'Quitar todo' : 'Agregar todo'}
    </Button>
  </Box>
);

const AssignComponentsForm = ({
  open,
  onClose,
  vehiculo,                 // { vehid, vehmat, ... }
  defaultSelectedIds = [],  // array o Set de comid ya seleccionados
  onSubmit,                 // (selectedIds: number[]) => void
}) => {
  const [q, setQ] = useState('');
  const vehId = Number(vehiculo?.vehid);

  // Trae grupos [{ sisvehid, sistemanombre, count, items: [...] }]
  const { components: groups = [], isLoading } = useComponents(vehId);

  // Estado plano de selección
  const [selected, setSelected] = useState(() => {
    const base = defaultSelectedIds instanceof Set ? defaultSelectedIds : new Set(defaultSelectedIds ?? []);
    return new Set(base);
  });

  // Búsqueda (aplicada por nombre del componente)
  const filteredGroups = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return groups;
    return groups.map(g => ({
      ...g,
      items: (g.items ?? []).filter(c => c.comnom?.toLowerCase().includes(term))
    }));
  }, [groups, q]);

  const totalItems = useMemo(
    () => groups.reduce((acc, g) => acc + (g.items?.length ?? 0), 0),
    [groups]
  );

  const totalSelected = selected.size;

  const toggleOne = (comid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(comid)) next.delete(comid);
      else next.add(comid);
      return next;
    });
  };

  const toggleAllInGroup = (g) => {
    const ids = (g.items ?? []).map(c => c.comid);
    const allSelected = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const toggleAllGlobal = () => {
    const allIds = groups.flatMap(g => g.items ?? []).map(c => c.comid);
    const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
    setSelected(() => (allSelected ? new Set() : new Set(allIds)));
  };

  const handleSubmit = () => {
    onSubmit?.(Array.from(selected));
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Asignar Componentes — {vehiculo?.vehmat || `Veh ${vehiculo?.vehid}`}
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" gap={2} mb={2} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar componente…"
            value={q}
            onChange={e => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={totalItems > 0 && totalSelected === totalItems}
                indeterminate={totalSelected > 0 && totalSelected < totalItems}
                onChange={toggleAllGlobal}
              />
            }
            label={`Seleccionar todo (${totalSelected}/${totalItems})`}
          />
        </Box>

        <Box display="flex" gap={2} mt={1}>
          <Paper sx={{ flex: 1, p: 2, backgroundColor: "#f9f9f9", borderRadius: 2, boxShadow: 3 }}>
            {isLoading ? (
              <Typography>Cargando…</Typography>
            ) : filteredGroups.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Sin resultados.</Typography>
            ) : (
              filteredGroups.map(g => {
                const count = g.items?.length ?? 0;
                const selectedCount = (g.items ?? []).reduce((acc, c) => acc + (selected.has(c.comid) ? 1 : 0), 0);

                return (
                  <Box key={`grp-${g.sisvehid}`} sx={{ mb: 2 }}>
                    <SystemHeader
                      name={g.sistemanombre}
                      count={count}
                      selectedCount={selectedCount}
                      onToggleAll={() => toggleAllInGroup(g)}
                    />
                    <Divider sx={{ mb: 1 }} />
                    {count === 0 ? (
                      <Typography variant="body2" color="text.secondary">Sin componentes en este sistema.</Typography>
                    ) : (
                      (g.items ?? []).map(c => (
                        <Box
                          key={`item-${c.comid}`}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ p: 1, mb: 1, borderRadius: 2, backgroundColor: '#fff', boxShadow: 1 }}
                        >
                          <Typography>{c.comnom}</Typography>
                          <Checkbox
                            checked={selected.has(c.comid)}
                            onChange={() => toggleOne(c.comid)}
                          />
                        </Box>
                      ))
                    )}
                  </Box>
                );
              })
            )}
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isLoading}>
          Guardar selección ({totalSelected})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignComponentsForm;
